import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Zap, MapPin, Router, Users, 
  Activity, Wifi, Phone, Globe, Headphones,
  Star, X
} from 'lucide-react';
import { slideDown } from '../animations';
import { trackClick, trackEvent } from '../../../utils/analytics';

/**
 * AboutUsMegaMenu - Refined mega menu for About Us navigation
 * with enhanced design and close functionality
 */
const AboutUsMegaMenu = ({
  isOpen,
  onClose,
  openRouterFeaturesModal,
  openISPFeaturesModal
}) => {
  // Reference to the menu container for click outside detection
  const menuRef = useRef(null);
  
  // Handle click outside to close menu
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose && onClose();
      }
    };
    
    // Track menu open event
    trackEvent('about_menu_opened', { timestamp: new Date().toISOString() });
    
    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  const handleMenuItemClick = (itemName) => {
    trackClick(itemName, 'mega_menu');
    onClose && onClose(); // Close menu after selection
  };
  
  return (
    <>
      {/* Transparent overlay to capture clicks outside the menu */}
      <div
        className="fixed inset-0 z-[48]"
        onClick={onClose}
      />
      
      <div
        className="fixed top-16 left-0 w-full z-[49]"
        style={{ animation: `${slideDown} 0.3s cubic-bezier(0.21, 1.02, 0.73, 1)` }}
        ref={menuRef}
      >
        {/* Solid background with subtle texture */}
        <div className="absolute inset-0 bg-gray-900 shadow-2xl"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1MDAiIGhlaWdodD0iNTAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii4wNSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoNTAwdjUwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=')] opacity-10 mix-blend-soft-light"></div>
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-8 py-8">
          {/* Header with close button */}
          <div className="flex items-center justify-between mb-6">
            {/* Close button - positioned at top right */}
            <button 
              onClick={onClose}
              className="absolute top-3 right-8 p-1.5 rounded-full bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-gray-600 text-gray-400 hover:text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
              aria-label="Close menu"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Main content with 3-column layout */}
          <div className="flex">
            {/* Left 2 columns for menu items and switch card */}
            <div className="w-2/3 pr-8">
              {/* Menu items in 2-column grid */}
              <div className="grid grid-cols-2 gap-8">
                {/* Left Column - About Us Content */}
                <div>
                  <div className="mb-5">
                    <div className="flex items-center space-x-3 mb-1.5">
                      <div className="h-[3px] w-8 bg-gradient-to-r from-purple-500 to-violet-600 rounded-full"></div>
                      <h3 className="font-semibold text-white text-sm tracking-wide">About Wondernet</h3>
                    </div>
                    <p className="text-gray-400 text-xs pl-11">Learn more about our company and services</p>
                  </div>
                  
                  <div className="space-y-3">
                    <Link
                      to="/why-choose-wondernet"
                      className="flex items-center p-3.5 rounded-lg transition-all duration-300 group hover:bg-gray-800 border border-transparent hover:border-gray-700/60 transform hover:-translate-y-0.5"
                      onClick={() => handleMenuItemClick('why_choose_wondernet')}
                    >
                      <div className="p-2.5 rounded-lg bg-gray-800 text-purple-400 group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:to-fuchsia-700 group-hover:text-white group-hover:shadow-lg group-hover:shadow-purple-700/20 transition-all duration-300">
                        <MapPin className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <div className="ml-3.5 flex-1">
                        <div className="font-medium text-white text-sm">Why Choose Wondernet</div>
                        <div className="text-xs text-gray-400 mt-0.5 group-hover:text-gray-300 transition-colors">Experience the Wondernet difference</div>
                      </div>
                      <div className="ml-auto transform translate-x-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 ease-out">
                        <ArrowRight className="w-4 h-4 text-purple-400" strokeWidth={2.5} />
                      </div>
                    </Link>
                    
                    <Link
                      to="/our-network"
                      className="flex items-center p-3.5 rounded-lg transition-all duration-300 group hover:bg-gray-800 border border-transparent hover:border-gray-700/60 transform hover:-translate-y-0.5"
                      onClick={() => handleMenuItemClick('our_network')}
                    >
                      <div className="p-2.5 rounded-lg bg-gray-800 text-purple-400 group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:to-fuchsia-700 group-hover:text-white group-hover:shadow-lg group-hover:shadow-purple-700/20 transition-all duration-300">
                        <Globe className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <div className="ml-3.5 flex-1">
                        <div className="font-medium text-white text-sm">Our Network</div>
                        <div className="text-xs text-gray-400 mt-0.5 group-hover:text-gray-300 transition-colors">Nationwide coverage and reliability</div>
                      </div>
                      <div className="ml-auto transform translate-x-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 ease-out">
                        <ArrowRight className="w-4 h-4 text-purple-400" strokeWidth={2.5} />
                      </div>
                    </Link>
                    
                    <Link
                      to="/our-team"
                      className="flex items-center p-3.5 rounded-lg transition-all duration-300 group hover:bg-gray-800 border border-transparent hover:border-gray-700/60 transform hover:-translate-y-0.5"
                      onClick={() => handleMenuItemClick('our_team')}
                    >
                      <div className="p-2.5 rounded-lg bg-gray-800 text-purple-400 group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:to-fuchsia-700 group-hover:text-white group-hover:shadow-lg group-hover:shadow-purple-700/20 transition-all duration-300">
                        <Users className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <div className="ml-3.5 flex-1">
                        <div className="font-medium text-white text-sm">Our Team</div>
                        <div className="text-xs text-gray-400 mt-0.5 group-hover:text-gray-300 transition-colors">Meet the people behind Wondernet</div>
                      </div>
                      <div className="ml-auto transform translate-x-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 ease-out">
                        <ArrowRight className="w-4 h-4 text-purple-400" strokeWidth={2.5} />
                      </div>
                    </Link>
                    
                    <Link
                      to="/contact-us"
                      className="flex items-center p-3.5 rounded-lg transition-all duration-300 group hover:bg-gray-800 border border-transparent hover:border-gray-700/60 transform hover:-translate-y-0.5"
                      onClick={() => handleMenuItemClick('contact_us')}
                    >
                      <div className="p-2.5 rounded-lg bg-gray-800 text-purple-400 group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:to-fuchsia-700 group-hover:text-white group-hover:shadow-lg group-hover:shadow-purple-700/20 transition-all duration-300">
                        <Phone className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <div className="ml-3.5 flex-1">
                        <div className="font-medium text-white text-sm">Contact Us</div>
                        <div className="text-xs text-gray-400 mt-0.5 group-hover:text-gray-300 transition-colors">Get in touch with our team</div>
                      </div>
                      <div className="ml-auto transform translate-x-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 ease-out">
                        <ArrowRight className="w-4 h-4 text-purple-400" strokeWidth={2.5} />
                      </div>
                    </Link>
                  </div>
                </div>
                
                {/* Middle Column - Features */}
                <div>
                  <div className="mb-5">
                    <div className="flex items-center space-x-3 mb-1.5">
                      <div className="h-[3px] w-8 bg-gradient-to-r from-purple-500 to-violet-600 rounded-full"></div>
                      <h3 className="font-semibold text-white text-sm tracking-wide">Our Features</h3>
                    </div>
                    <p className="text-gray-400 text-xs pl-11">What makes our service stand out</p>
                  </div>
                  
                  <div className="space-y-3">
                    <button
                      className="flex items-center p-3.5 rounded-lg transition-all duration-300 group hover:bg-gray-800 border border-transparent hover:border-gray-700/60 transform hover:-translate-y-0.5 w-full text-left"
                      onClick={() => {
                        handleMenuItemClick('uncapped_fibre');
                        openISPFeaturesModal && openISPFeaturesModal();
                      }}
                    >
                      <div className="p-2.5 rounded-lg bg-gray-800 text-purple-400 group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:to-fuchsia-700 group-hover:text-white group-hover:shadow-lg group-hover:shadow-purple-700/20 transition-all duration-300">
                        <Wifi className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <div className="ml-3.5 flex-1">
                        <div className="font-medium text-white text-sm">Uncapped Fibre</div>
                        <div className="text-xs text-gray-400 mt-0.5 group-hover:text-gray-300 transition-colors">No throttling or shaping</div>
                      </div>
                      <div className="ml-auto transform translate-x-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 ease-out">
                        <ArrowRight className="w-4 h-4 text-purple-400" strokeWidth={2.5} />
                      </div>
                    </button>
                    
                    <button
                      className="flex items-center p-3.5 rounded-lg transition-all duration-300 group hover:bg-gray-800 border border-transparent hover:border-gray-700/60 transform hover:-translate-y-0.5 w-full text-left"
                      onClick={() => {
                        handleMenuItemClick('premium_router');
                        openRouterFeaturesModal && openRouterFeaturesModal();
                      }}
                    >
                      <div className="p-2.5 rounded-lg bg-gray-800 text-purple-400 group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:to-fuchsia-700 group-hover:text-white group-hover:shadow-lg group-hover:shadow-purple-700/20 transition-all duration-300">
                        <Router className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <div className="ml-3.5 flex-1">
                        <div className="font-medium text-white text-sm">Free Premium Router</div>
                        <div className="text-xs text-gray-400 mt-0.5 group-hover:text-gray-300 transition-colors">Whole-home WiFi coverage</div>
                      </div>
                      <div className="ml-auto transform translate-x-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 ease-out">
                        <ArrowRight className="w-4 h-4 text-purple-400" strokeWidth={2.5} />
                      </div>
                    </button>
                    
                    <Link
                      to="/features/wonderwatch"
                      className="flex items-center p-3.5 rounded-lg transition-all duration-300 group hover:bg-gray-800 border border-transparent hover:border-gray-700/60 transform hover:-translate-y-0.5"
                      onClick={() => handleMenuItemClick('wonderwatch')}
                    >
                      <div className="p-2.5 rounded-lg bg-gray-800 text-purple-400 group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:to-fuchsia-700 group-hover:text-white group-hover:shadow-lg group-hover:shadow-purple-700/20 transition-all duration-300">
                        <Activity className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <div className="ml-3.5 flex-1">
                        <div className="font-medium text-white text-sm">WonderWatch™ Monitoring</div>
                        <div className="text-xs text-gray-400 mt-0.5 group-hover:text-gray-300 transition-colors">24/7 network monitoring</div>
                      </div>
                      <div className="ml-auto transform translate-x-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 ease-out">
                        <ArrowRight className="w-4 h-4 text-purple-400" strokeWidth={2.5} />
                      </div>
                    </Link>
                    
                    <Link
                      to="/contact-us"
                      className="flex items-center p-3.5 rounded-lg transition-all duration-300 group hover:bg-gray-800 border border-transparent hover:border-gray-700/60 transform hover:-translate-y-0.5"
                      onClick={() => handleMenuItemClick('expert_support')}
                    >
                      <div className="p-2.5 rounded-lg bg-gray-800 text-purple-400 group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:to-fuchsia-700 group-hover:text-white group-hover:shadow-lg group-hover:shadow-purple-700/20 transition-all duration-300">
                        <Headphones className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <div className="ml-3.5 flex-1">
                        <div className="font-medium text-white text-sm">Expert Support</div>
                        <div className="text-xs text-gray-400 mt-0.5 group-hover:text-gray-300 transition-colors">Always ready to help</div>
                      </div>
                      <div className="ml-auto transform translate-x-0 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 ease-out">
                        <ArrowRight className="w-4 h-4 text-purple-400" strokeWidth={2.5} />
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
              
              {/* Switch to Wondernet Card - Spanning across both menu columns */}
              <div className="mt-3">
                <Link
                  to="/switch"
                  onClick={() => handleMenuItemClick('how_to_switch')}
                  className="block rounded-lg border border-gray-700/40 hover:border-purple-500/30 bg-gray-800/80 hover:bg-gray-800 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg hover:shadow-purple-900/5 group"
                >
                  <div className="p-4 flex items-center">
                    {/* Left side with icon */}
                    <div className="flex-shrink-0 mr-4">
                      <div className="p-3 rounded-lg bg-purple-600/10 group-hover:bg-purple-600/20 text-purple-400 group-hover:text-purple-300 transition-all duration-300">
                        <ArrowRight className="w-5 h-5" strokeWidth={2} />
                      </div>
                    </div>
                    
                    {/* Middle content */}
                    <div className="flex-1">
                      <h3 className="text-base font-medium text-white mb-1 group-hover:text-purple-100 transition-colors">How to Switch to Wondernet</h3>
                      <p className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors">Simple steps to join our network with no downtime. We handle everything for you.</p>
                    </div>
                    
                    {/* Right arrow indicator */}
                    <div className="ml-4 flex-shrink-0">
                      <div className="p-1.5 rounded-full bg-gray-700/50 group-hover:bg-purple-600/20 transition-all duration-300">
                        <ArrowRight className="w-4 h-4 text-purple-400 group-hover:text-purple-300 transition-colors" />
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
            
            {/* Right Column - Promo */}
            <div className="w-1/3 space-y-6">
              <div className="relative overflow-hidden p-6 bg-gradient-to-br from-pink-600 via-fuchsia-600 to-purple-600 rounded-xl text-white group transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-purple-900/20 border border-white/5">
                {/* Background effects */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mt-20 -mr-20 transition-transform group-hover:scale-110" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -mb-12 -ml-12 transition-transform group-hover:scale-110" />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="relative">
                    <div className="absolute -inset-1 bg-white/10 rounded-lg blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative p-3 bg-white/20 backdrop-blur-sm rounded-lg w-14 h-14 flex items-center justify-center mb-4 shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                      <Zap className="w-7 h-7 text-white" strokeWidth={2} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-2.5 tracking-tight">Save up to R5,000</h3>
                  <p className="text-white/90 text-sm mb-5 max-w-xs leading-relaxed">Free installation, activation and WiFi router included with every fibre package.</p>
                  <button
                    className="inline-flex items-center bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white py-2 px-4 rounded-lg transition-all duration-300 group/btn"
                    onClick={() => {
                      handleMenuItemClick('promotions');
                      openISPFeaturesModal && openISPFeaturesModal();
                    }}
                  >
                    <span className="text-sm font-medium">Learn More</span>
                    <ArrowRight className="w-4 h-4 ml-2 transform group-hover/btn:translate-x-1 transition-transform duration-300" />
                  </button>
                </div>
              </div>
              
              <div className="relative overflow-hidden p-6 bg-gray-800 rounded-xl border border-gray-700/60 group transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-purple-900/5">
                {/* Background patterns */}
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-purple-500/5 rounded-full opacity-0 group-hover:opacity-100 transform group-hover:scale-110 transition-all duration-500"></div>
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-purple-500/3 rounded-full opacity-0 group-hover:opacity-100 transform group-hover:scale-110 transition-all duration-500"></div>
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="p-2.5 bg-purple-600/20 rounded-lg w-12 h-12 flex items-center justify-center mb-4 transform group-hover:scale-110 transition-transform">
                    <Star className="w-5 h-5 text-purple-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2.5">Find Your Perfect Package</h3>
                  <p className="text-gray-300 text-sm mb-4 leading-relaxed">Answer a few questions to find your ideal connection based on your needs.</p>
                  <Link
                    to="/recommend"
                    className="flex items-center text-purple-400 hover:text-purple-300 group/btn transition-all"
                    onClick={() => handleMenuItemClick('find_perfect_package')}
                  >
                    <span className="text-sm font-medium border-b border-purple-500/30 pb-0.5 group-hover/btn:border-purple-500/60 transition-all">Start Now</span>
                    <ArrowRight className="w-4 h-4 ml-2 transform group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutUsMegaMenu;